import { defaultBalance, levelMap, LEVEL_ID_RE, type ArchetypeId, type BossAbility, type ChestId, type EnemyArchId, type GearSlot, type HeroId, type MapDef, type ModifierId, type PerkId, type SkillBranch, type SkillId } from '@holdline/sim';
import bunkerJson from './themes/bunker.json';
import map01Json from './maps/map01.json';
import map02Json from './maps/map02.json';
import map03Json from './maps/map03.json';
import map04Json from './maps/map04.json';
import map05Json from './maps/map05.json';
import endless01Json from './maps/endless01.json';
import ruJson from './locales/ru.json';
import enJson from './locales/en.json';

export type Locale = 'ru' | 'en';
export type Localized = Record<Locale, string>;

/**
 * A theme pack is everything setting-specific. The simulation only knows archetype ids;
 * the theme says what they are called and how they look. Ship a second theme
 * (e.g. a meme setting) without touching a line of game logic.
 */
export interface ThemePack {
  id: string;
  title: Localized;
  units: Record<ArchetypeId, { name: Localized; desc: Localized; color: string }>;
  /** Only spawner archetypes have an entry. */
  soldiers: Partial<Record<ArchetypeId, { name: Localized; color: string }>>;
  enemies: Record<EnemyArchId, { name: Localized; color: string; radius: number; desc: Localized }>;
  wall: { name: Localized };
  closedCell: { name: Localized };
  perks: Record<PerkId, { name: Localized; desc: Localized }>;
  heroes: Record<HeroId, { name: Localized; color: string; skill: Localized; desc: Localized }>;
  /** What each gear slot is called in this setting; the item name is "<rarity> <slot>". Shared by all heroes. */
  gear: Record<GearSlot, { name: Localized }>;
  shop: Record<ChestId, { name: Localized; desc: Localized }>;
  /** Skill tree: branch titles and node names; desc has {n} for the per-level step. */
  skills: { branches: Record<SkillBranch, Localized>; nodes: Record<SkillId, { name: Localized; desc: Localized }> };
  /** Challenge modifiers and the names of the challenge templates in balance.challenges.pool. */
  modifiers: Record<ModifierId, { name: Localized; desc: Localized }>;
  challenges: Record<string, { name: Localized }>;
  bossAbilities: Record<BossAbility, { name: Localized; desc: Localized }>;
}

export const themes: Record<string, ThemePack> = {
  bunker: bunkerJson as ThemePack,
};

export const maps: Record<string, MapDef> = {
  map01: map01Json as MapDef,
  map02: map02Json as MapDef,
  map03: map03Json as MapDef,
  map04: map04Json as MapDef,
  map05: map05Json as MapDef,
  endless01: endless01Json as MapDef,
};

/** Display order in the Levels tab; each map's `requires` says what must be won first. */
export const campaign: string[] = ['map01', 'map02', 'map03', 'map04', 'map05', 'endless01'];

const generated = new Map<string, MapDef>();

/** Any playable map by id: an authored one, or a ladder level `l<n>` built on demand and cached. */
export function resolveMap(id: string): MapDef | undefined {
  const authored = maps[id];
  if (authored) return authored;
  const m = LEVEL_ID_RE.exec(id);
  if (!m) return undefined;
  const cached = generated.get(id);
  if (cached) return cached;
  const built = levelMap(Number(m[1]), defaultBalance);
  if (built) generated.set(id, built);
  return built ?? undefined;
}

const locales: Record<Locale, Record<string, string>> = {
  ru: ruJson,
  en: enJson,
};

export function makeT(locale: Locale): (key: string, vars?: Record<string, string | number>) => string {
  const dict = locales[locale];
  return (key, vars) => {
    let s = dict[key] ?? locales.en[key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
    }
    return s;
  };
}

export function pickLocale(languageCode: string | undefined): Locale {
  return languageCode?.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}
